import { Typography } from "@suraasa/placebo-ui"

type ModuleProgressItem = {
  label: string
  value: number
  color: string
}

type ModuleProgressBarProps = {
  title: string
  completed: number
  totalProgress: number
  items: ModuleProgressItem[]
}

const ModuleProgressBar = ({
  title,
  completed,
  totalProgress,
  items,
}: ModuleProgressBarProps) => {
  const filled = items.reduce((acc, item) => acc + item.value, 0)
  const remaining = totalProgress - filled

  const fullItems = [...items, { value: remaining, color: "bg-onSurface-200" }]
  const showDefaultBar = fullItems.every(item => item.value === 0)
  const displayItems = showDefaultBar
    ? [{ value: 1, color: "bg-onSurface-200" }]
    : fullItems

  return (
    <div className="flex size-full flex-col justify-between p-2.5">
      <Typography className="mb-5 text-muted" variant="strongSmallBody">
        {title}
      </Typography>

      <div>
        <Typography className="text-onSurface-900" variant="title1">
          {completed}{" "}
          <span className="text-strong text-onSurface-400">
            / {totalProgress} done
          </span>
        </Typography>

        {/* Progress bar */}
        <div className="mt-2 flex h-1 w-full overflow-hidden rounded-full sm:w-3/4 md:w-full">
          {displayItems.map((item, i) => (
            <div
              key={i}
              className={`${item.color} h-full rounded-full transition-all duration-700`}
              style={{
                width:
                  totalProgress > 0
                    ? `${(item.value / totalProgress) * 100}%`
                    : showDefaultBar
                    ? "100%"
                    : "0%",
                marginLeft: i === 0 ? 0 : "1px",
                marginRight: i === displayItems.length - 1 ? 0 : "1px",
              }}
            />
          ))}
        </div>

        {/* Labels */}
        <div className="mt-1.5 flex flex-wrap gap-x-2.5 gap-y-0.5 text-sm">
          {items.map((item, i) => (
            <div key={i} className="flex items-center space-x-1">
              <div
                className={`size-1.75 rounded-full ${item.color} shrink-0`}
              />
              <Typography
                className="shrink-0 font-medium text-muted"
                variant="smallBody"
              >
                {item.label}{" "}
                <span className="shrink-0 text-onSurface-900">
                  {item.value}
                </span>
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ModuleProgressBar
